import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ServiceType } from '../types'
import { getServiceConfig } from '../data/services'

export interface NavigationState {
  currentService?: ServiceType
  isScrolled: boolean
  showMobileMenu: boolean
  breadcrumbPath: Array<{ label: string; href: string; current?: boolean }>
}

export const useNavigation = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [isScrolled, setIsScrolled] = useState(false)
  const [showMobileMenu, setShowMobileMenu] = useState(false)

  // Handle scroll effect for navbar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Determine current service from URL
  const getCurrentService = (): ServiceType | undefined => {
    const path = location.pathname
    if (path.startsWith('/hr')) return ServiceType.HR
    if (path.startsWith('/compliance')) return ServiceType.COMPLIANCE
    if (path.startsWith('/trade')) return ServiceType.TRADE
    if (path.startsWith('/intelligence')) return ServiceType.INTELLIGENCE
    return undefined
  }

  // Generate breadcrumb path based on current location
  const getBreadcrumbPath = () => {
    const currentService = getCurrentService()
    if (!currentService) return []

    const service = getServiceConfig(currentService)
    return [
      {
        label: 'Services',
        href: '/#services'
      },
      {
        label: service.name,
        href: service.baseUrl || '#',
        current: true
      }
    ]
  }

  // Navigation handlers
  const handleServiceNavigation = (serviceType: ServiceType) => {
    const service = getServiceConfig(serviceType)
    if (service.isActive && service.baseUrl) {
      navigate(service.baseUrl)
    } else {
      // Redirect to main page with services section for coming soon services
      navigate('/#services')
    }
  }

  const handleToggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu)
  }

  const handleCloseMobileMenu = () => {
    setShowMobileMenu(false)
  }

  // Navigation state
  const navigationState: NavigationState = {
    currentService: getCurrentService(),
    isScrolled,
    showMobileMenu,
    breadcrumbPath: getBreadcrumbPath()
  }

  return {
    navigationState,
    handleServiceNavigation,
    handleToggleMobileMenu,
    handleCloseMobileMenu,
    navigate,
    location
  }
}